/**
 * The component registry: custom element tag -> the module that defines it.
 *
 * Two files read this, and they read it for different reasons:
 *
 *   - `bootstrap.js` uses it to decide what to import, by looking at what is
 *     actually in the DOM. That is the whole loading strategy (see
 *     `docs/component-architecture.md`, «Cómo se carga»).
 *   - `component.js` uses it to recognise an element that is *meant* to be a
 *     component but whose module has not executed yet, so a click landing in
 *     that window can still be resolved instead of silently dropped.
 *
 * It lives in its own file precisely because of the second reader: `component.js`
 * must not import `bootstrap.js` (which imports components, which import
 * `component.js`). This module has no static imports of its own — the values are
 * arrow functions, so nothing here pulls in a component until it is called.
 *
 * A Map rather than an object literal: `has()` answers only for keys we put
 * there, where `'toString' in obj` would answer true.
 *
 * @type {Map<string, () => Promise<unknown>>}
 */
export const COMPONENTS = new Map([
  ['collection-pagination', () => import('@theme/collection-pagination')],
  ['countdown-timer', () => import('@theme/countdown')],
  ['hero-slider', () => import('@theme/hero-slider')],
  ['pdp-carousel', () => import('@theme/pdp-carousel')],
  ['product-accordion', () => import('@theme/product-accordion')],
  ['product-detail', () => import('@theme/product-detail')],
  ['product-gift-card-recipient', () => import('@theme/product-gift-card-recipient')],
  ['product-notify-drawer', () => import('@theme/product-notify-drawer')],
  ['product-media-frame', () => import('@theme/product-media')],
  ['product-quick-add', () => import('@theme/quick-add')],
  ['product-options', () => import('@theme/product-options')],
  ['product-price', () => import('@theme/product-price')],
  ['product-variants-scroll', () => import('@theme/product-variants-scroll')],
  ['size-guide', () => import('@theme/size-guide')],
]);

/**
 * Whether a tag name belongs to a component this theme knows how to load.
 *
 * @param {string} tag - A lowercase tag name.
 * @returns {boolean}
 */
export function isComponentTag(tag) {
  return COMPONENTS.has(tag);
}
